import React from 'react';

import { Card, CardActionArea, Radio, Stack, useRadioGroup } from '@mui/material';
import type { CardActionAreaProps, CardProps, StackProps } from '@mui/material';
import { sxMerge } from '../utils/libs';

export type RadioCardProps = {
  value: string;
  selected?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  hideRadio?: boolean;
  disableActiveState?: boolean;
  innerContainerProps?: StackProps;
  radioContainerProps?: StackProps;
  cardActionAreaProps?: Omit<CardActionAreaProps, 'onClick'>;
} & Omit<CardProps, 'onClick'>;

const RadioCard = ({
  selected,
  value,
  children,
  onClick,
  hideRadio,
  disableActiveState,
  innerContainerProps,
  radioContainerProps,
  cardActionAreaProps,
  ...cardProps
}: RadioCardProps) => {
  const radioGroup = useRadioGroup();
  const checked = selected ?? radioGroup?.value === value;

  const handleClick = React.useCallback<React.MouseEventHandler<HTMLElement>>(
    (event) => {
      onClick?.(event);
      const syntheticEvent = { target: { value } } as unknown as React.ChangeEvent<HTMLInputElement>;
      radioGroup?.onChange?.(syntheticEvent, value);
    },
    [onClick, value, radioGroup],
  );

  return (
    <Card {...cardProps}>
      <CardActionArea
        onClick={handleClick}
        data-active={!disableActiveState && checked ? '' : undefined}
        {...cardActionAreaProps}
        sx={sxMerge(
          { width: '100%', height: '100%' },
          !disableActiveState && {
            // From https://v6.mui.com/material-ui/react-card/#active-state-styles
            '&[data-active]': {
              'backgroundColor': 'action.selected',
              '&:hover': { backgroundColor: 'action.selectedHover' },
            },
          },
          cardActionAreaProps?.sx,
        )}
      >
        <Stack flexDirection="row" {...innerContainerProps}>
          <Stack
            padding={1}
            {...radioContainerProps}
            sx={sxMerge(hideRadio && { display: 'none' }, radioContainerProps?.sx)}
          >
            <Radio value={value} checked={checked} />
          </Stack>

          {children}
        </Stack>
      </CardActionArea>
    </Card>
  );
};

export default RadioCard;
